import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import SearchFieldTsoPq from '@components/SearchField/SearchFieldTsoPq';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { GridColDef, GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useRoleCheck } from '@utils/hooks/useRoleCheck';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  customSortIconAsc,
  customSortIconDesc,
  StyledActionsWrapper,
  StyledDataGrid,
  StyledGridWrapper,
  StyledHeaderWrapper,
  StyledIcon,
  StyledPageWrapper,
  StyledTitle,
} from './StyledGridPageComponents';

type GenericGridPageProps<T extends GridValidRowModel> = {
  title: string;
  rows: T[];
  columns: GridColDef<GridValidRowModel>[];
  onAddClick: () => void;
  modalComponent?: ReactElement | null;
  searchLabel?: string;
  sortModel?: GridSortModel;
};

export default function GenericGridPage<T extends GridValidRowModel>(
  props: Readonly<GenericGridPageProps<T>>
): ReactElement {
  const { title, rows, columns, onAddClick, modalComponent, searchLabel, sortModel } = props;
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchKey, setSearchKey] = useState<'label' | 'id'>('label');
  const [internalSortModel, setInternalSortModel] = useState<GridSortModel>(sortModel ?? []);

  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  useEffect(() => {
    setSearchKey('label');
  }, []);

  const filteredRows = rows.filter((row) =>
    Object.values(row).join(' ').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columnVisibilityModel = useMemo(
    () => ({
      edit: supportOrUserAllowed,
    }),
    []
  );

  return (
    <StyledPageWrapper>
      <StyledHeaderWrapper>
        <StyledTitle>{title}</StyledTitle>
        <StyledActionsWrapper>
          <SearchFieldTsoPq
            searchQuery={searchQuery}
            searchKey={searchKey}
            handleChange={setSearchQuery}
            resetSearch={() => setSearchQuery('')}
            customLabel={searchLabel ?? t('searchfield.search')}
          />
          <PermissionGate roles={userOrSupportRestriction}>
            <ButtonTsoPq
              iconLeft={<StyledIcon icon={faPlus} />}
              sizeVariant="m"
              buttonType="filledPrimary"
              textContent={title}
              onClick={onAddClick}
            />
          </PermissionGate>
        </StyledActionsWrapper>
      </StyledHeaderWrapper>
      <StyledGridWrapper>
        <StyledDataGrid
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row')}
          rows={filteredRows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnReorder
          disableColumnPinning
          hideFooterSelectedRowCount
          isCellEditable={() => false}
          hideFooter
          localeText={{
            noRowsLabel: t('general.grid.noRows'),
            noResultsOverlayLabel: t('general.grid.noMatchingRows'),
          }}
          getRowHeight={() => 47}
          getRowId={(row) => row.id}
          showCellVerticalBorder
          slots={{ columnSortedAscendingIcon: customSortIconAsc, columnSortedDescendingIcon: customSortIconDesc }}
          disableColumnMenu
          sortModel={internalSortModel}
          columnVisibilityModel={columnVisibilityModel}
          onSortModelChange={(model) => setInternalSortModel(model)}
        />
      </StyledGridWrapper>
      {modalComponent}
    </StyledPageWrapper>
  );
}
